import { Box, Grid, TextField, Typography } from '@mui/material';
import { Autocomplete } from '@mui/material';
import React, {Ref} from 'react';
import { AdvancedSearch } from '../../model/User';
import { InventoryAdvancedSearchParams } from "../../model/Inventory";
import { FormikProps } from "formik";
import {LocalizationProvider} from "@mui/x-date-pickers/LocalizationProvider";
import {AdapterDateFns} from "@mui/x-date-pickers/AdapterDateFns";
import {DesktopDatePicker} from "@mui/x-date-pickers-pro";
import {formatDateToISO, isValidDate} from "../../util/date";

export interface AdvancedSearchFormProps {
    advancedSearches: Array<AdvancedSearch>;
    searchValues: InventoryAdvancedSearchParams;
    submitButtonRef?: Ref<HTMLButtonElement>;
}

export type AdvancedSearchFormParamType = {
    [key: string]: string;
}

export interface AdvancedSearchFormParams {
    selectedOption: AdvancedSearch | undefined;
    parameters: AdvancedSearchFormParamType;
}

export default function AdvancedSearchForm(props: AdvancedSearchFormProps & FormikProps<AdvancedSearchFormParams>): JSX.Element {
    const {
        values,
        handleSubmit,
        setFieldValue,
        advancedSearches,
        submitButtonRef,
    } = props;

    const handleOptionChange = (event: React.SyntheticEvent, newOption: AdvancedSearch) => {
        setFieldValue("selectedOption", newOption);
        setFieldValue("parameters", {}); // Reset user entered params after changing options
    }

    const handleParamChange = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>, id: string) => {
        setFieldValue(`parameters.${id}`, event.target.value);
    }

    const handleDateParamChange = (value: Date | null, id: string) => {
        setFieldValue(`parameters.${id}`, formatDateToISO(value));
    }

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box>
                <form onSubmit={handleSubmit}>
                    <button ref={submitButtonRef} type="submit" style={{display: "none"}}/>
                    <Grid>
                        <Autocomplete
                            id="selectedOption"
                            options={advancedSearches}
                            getOptionLabel={(option: AdvancedSearch) => option.name}
                            renderInput={(params) => <TextField {...params} variant="standard" label="Quick Query" required />}
                            onChange={handleOptionChange}
                            value={values.selectedOption}
                            disableClearable
                            sx={{width: 400}}
                        />
                    </Grid>
                    <Typography variant="caption">
                        {values.selectedOption?.description}
                    </Typography>
                    {values.selectedOption?.parameters?.map((param, index) => {
                        if (param.type !== undefined && param.type === "date") {
                            return (
                                <Grid item xs={12} key={param.code}>
                                    <DesktopDatePicker
                                        label={param.name}
                                        inputFormat="dd/MM/yyyy"
                                        value={values.parameters[param.code] !== undefined ? values.parameters[param.code] : ""}
                                        onChange={(newValue: Date | null) => handleDateParamChange(newValue, param.code)}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                variant="standard"
                                                sx={{width: 400}}
                                            />
                                        )}
                                    />
                                </Grid>
                            );
                        }

                        return (
                            <Grid item xs={12} key={param.code}>
                                <TextField
                                    id={param.code}
                                    variant="standard"
                                    label={param.name}
                                    sx={{width: 400}}
                                    value={values.parameters[param.code] !== undefined ? values.parameters[param.code] : ""}
                                    onChange={(event) => handleParamChange(event, param.code)}
                                    inputProps={{
                                        autoComplete: "off",
                                    }}
                                />
                            </Grid>
                        );
                    })}
                </form>
            </Box>
        </LocalizationProvider>
    );
}
